import { Component, Inject, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from "@angular/material/dialog";
import { DataStorageService } from "../../services/data-storage.service";
import { Claim, FnolRequest, Invoice, NotificationDto, PetInsuranceData, SideModalCloseAction, SideModalComponentTypeEnum } from "../../models";
import { Router } from "@angular/router";
import { delay, Observable, of } from "rxjs";
import { ApiHttpService } from "../../services/api-http.service";

@Component({
  selector: 'cpf-side-modal',
  templateUrl: './side-modal.component.html',
  styleUrls: ['./side-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SideModalComponent implements OnInit {
  @ViewChild('renderedSubcomponent') renderedSubcomponent?: any;
  public petData?: PetInsuranceData;
  public claim?: Claim;
  public invoice?: Invoice;
  public fnolRequest?: FnolRequest;
  public chatClaimDetailsItem?: any;
  public voucherDetailsItem?: NotificationDto;
  public SideModalComponentTypeEnum = SideModalComponentTypeEnum;
  public SideModalCloseAction = SideModalCloseAction;

  constructor(public dialogRef: MatDialogRef<SideModalComponent>,
              @Inject(MAT_DIALOG_DATA) public data: {
                type: SideModalComponentTypeEnum,
                claimId?: string,
                invoiceId?: string,
                fnolRequestId?: string,
                chatClaimDetailsType?: 'chat' | 'claim',
                chatClaimDetailsId?: string,
                voucherId?: string,
              },
              public dataStorageService: DataStorageService,
              public dialog: MatDialog,
              private router: Router,
              private apiHttpService: ApiHttpService) {
  }

  ngOnInit(): void {
    this.petData = this.dataStorageService.selectedData;

    if (this.data.claimId) {
      this.petData = this.findPetByClaimId(this.data.claimId);
      this.claim = this.petData?.claims?.find(claim => claim.identifier === this.data.claimId);
    }

    if (this.data.invoiceId) {
      this.petData = this.findPetByInvoiceId(this.data.invoiceId);
      this.invoice = this.petData?.invoices?.find(invoice => invoice.invoiceNumber === this.data.invoiceId);
    }

    if (this.data.fnolRequestId) {
      this.fnolRequest = this.findFnolRequestById(this.data.fnolRequestId);
    }

    if (this.data.chatClaimDetailsType && this.data.chatClaimDetailsId) {
      this.prepareChatClaimDetailsItem(this.data.chatClaimDetailsType, this.data.chatClaimDetailsId);
    }

    if (this.data.voucherId) {
      this.prepareVoucherDetailsItem(this.data.voucherId);
    }
  }

  private findPetByInvoiceId(invoiceId: string): PetInsuranceData | undefined {
    return this.dataStorageService.getAllData
      .find(pet => pet.invoices?.some(invoice => invoice.invoiceNumber === invoiceId));
  }

  private findPetByClaimId(claimId: string): PetInsuranceData | undefined {
    return this.dataStorageService.getAllData
      .find(pet => pet.claims?.some(claim => claim.identifier === claimId));
  }

  private findFnolRequestById(fnolRequestId: string) {
    return this.dataStorageService.fnolRequests.find(fR => fR.id === fnolRequestId);
  }

  private prepareChatClaimDetailsItem(type: 'chat' | 'claim', id: string) {
    const petData = this.dataStorageService.selectedData;
    let originalItem: any;
    let title = '';

    if (type === 'chat') {
      const chatSummaries = this.dataStorageService.chatSummaries;
      originalItem = chatSummaries.find(chat => chat.id === id);
      title = originalItem?.medical_category || 'Allgemeine Beratung';
    } else {
      const claims = petData?.claims || [];
      originalItem = claims.find(claim => claim.identifier === id);
      title = originalItem?.cause || '';
    }

    if (originalItem) {
      this.chatClaimDetailsItem = {
        id,
        title,
        type,
        petName: petData?.pet_name || 'Unbekannt',
        originalItem
      };
    }
  }

  private async prepareVoucherDetailsItem(voucherId: string) {
    const notifications = await this.apiHttpService.getUserNotifications();
    this.voucherDetailsItem = (notifications || []).find(notification => notification.id === voucherId);
  }

  canDeactivate(): Observable<boolean> | boolean {
    return this.renderedSubcomponent?.canDeactivate ? this.renderedSubcomponent.canDeactivate() : true;
  }

  close(action: SideModalCloseAction = SideModalCloseAction.NavigateHome, force?: boolean) {
    if (force) {
      this.dialogRef.close(action);
    } else {
      //use canDeactivate to check if it safe to close
      const canClose = this.canDeactivate();
      if (canClose instanceof Observable) {
        canClose.subscribe((result) => {
          if (result) {
            this.dialogRef.close(action);
          }
        });
      } else if (canClose) {
        this.dialogRef.close(action);
      }
    }
  }

  forceCloseAll() {
    this.dialog.closeAll();
  }

  editInsuranceClicked() {
    this.router.navigate(['desktop', 'home', 'pet', 'insurance', 'edit-insurance-info']);
  }

  editAccountClicked() {
    this.close(SideModalCloseAction.KeepCurrentRoute);
    this.router.navigate(['desktop', 'home', 'account', 'edit']);
  }

  changePasswordClicked() {
    this.close(SideModalCloseAction.KeepCurrentRoute);
    this.router.navigate(['desktop', 'home', 'account', 'change-password']);
  }

  changeIbanClicked() {
    this.close(SideModalCloseAction.KeepCurrentRoute);
    this.router.navigate(['desktop', 'home', 'account', 'change-iban']);
  }

  changePasswordCancelClicked() {
    this.close(SideModalCloseAction.KeepCurrentRoute);
    this.router.navigate(['desktop', 'home', 'account', 'view']);
  }

  changeIbanCancelClicked() {
    this.close(SideModalCloseAction.KeepCurrentRoute);
    this.router.navigate(['desktop', 'home', 'account', 'view']);
  }

  setIbanClicked() {
    this.close(SideModalCloseAction.KeepCurrentRoute, true);
    this.router.navigate(['desktop', 'home', 'account', 'change-iban']);
  }

  // Closing with KeepCurrentRoute leaves the url on .../change-password, and
  // re-opening the profile would then be a no-op navigation. Go back to the
  // account preview so the url matches the panel that is on screen.
  changePasswordSuccessClicked() {
    this.close(SideModalCloseAction.KeepCurrentRoute);
    this.router.navigate(['desktop', 'home', 'account', 'view']);
  }

  changeIbanSuccessClicked() {
    this.close(SideModalCloseAction.KeepCurrentRoute);
    this.router.navigate(['desktop', 'home', 'account', 'view']);
  }

  forgotPasswordClicked() {
    this.close(SideModalCloseAction.KeepCurrentRoute);
    this.router.navigate(['desktop', 'home', 'account', 'forgot-password']);
  }

  contactUsClicked() {
    this.close(SideModalCloseAction.KeepCurrentRoute);
    this.router.navigate(['desktop', 'home', 'account', 'contact-us']);
  }

  vouchersClicked() {
    this.close(SideModalCloseAction.KeepCurrentRoute);
    this.router.navigate(['desktop', 'home', 'account', 'vouchers']);
  }

  accountPreviewClicked() {
    this.close(SideModalCloseAction.KeepCurrentRoute);
    this.router.navigate(['desktop', 'home', 'account', 'view']);
  }

  backToAccountPreviewClicked() {
    this.forceCloseAll();

    // Delay need to wait when all modal windows will be forced closed (with no confirmation), if no wait confirmation modal shown
    of(null).pipe(delay(100)).subscribe(() => {
      this.router.navigate(['desktop', 'home', 'account', 'view']);
    });
  }

  chatHistoryClicked() {
    this.data.type = SideModalComponentTypeEnum.ChatHistory;
  }

  chatClicked() {
    this.data.type = SideModalComponentTypeEnum.Chat;
  }

  chatClaimDetailsClicked(item: any) {
    this.chatClaimDetailsItem = item;
    this.data.type = SideModalComponentTypeEnum.ChatClaimDetails;
  }

  voucherDetailsClicked(voucher: NotificationDto) {
    this.voucherDetailsItem = voucher;
    this.data.type = SideModalComponentTypeEnum.AccountVoucherDetails;
  }

  voucherDetailsBackClicked() {
    this.data.type = SideModalComponentTypeEnum.AccountVouchers;
  }
}
